import { useState } from "react";
import { Activity, Send, Loader2, TrendingUp } from "lucide-react";
import { launchpadApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import useRefreshOnFocus from "@/lib/useRefreshOnFocus";

// ProductivityPulseWidget — Feb 2026.
//
// Lives on Launchpad. Shows a 7-day SVG sparkline of tasks marked complete
// across the user's visible projects, total for the week, and top 3
// contributors. Admins also see a "Send Digest Now" button that posts to
// the weekly digest endpoint in dry-run mode (preview) or real mode (live).
//
// Backed by GET /api/launchpad/productivity-pulse. The sparkline is hand-
// rolled SVG to match the rest of the app's no-deps visualization story
// (same approach as the Gantt chart).

const SVG_W = 240;
const SVG_H = 56;
const PAD_X = 4;
const PAD_Y = 6;

export default function ProductivityPulseWidget({ isAdmin = false }) {
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useRefreshOnFocus(() => {
    let cancelled = false;
    launchpadApi
      .getPulse(7)
      .then((data) => { if (!cancelled) setPulse(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSendDigest = async (dryRun = false) => {
    setSending(true);
    try {
      const result = await launchpadApi.sendWeeklyDigest({
        dryRun,
        skipEmpty: !dryRun, // dry-run mode also includes empty users for visibility
      });
      const verb = dryRun ? "Preview computed" : "Digest sent";
      toast.success(
        `${verb}: ${result.sent} email${result.sent === 1 ? "" : "s"} ${
          dryRun ? "would be" : ""
        } delivered${
          result.skipped_empty > 0 ? ` · ${result.skipped_empty} skipped (zero tasks)` : ""
        }`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <article
        data-testid="productivity-pulse-loading"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5 flex items-center gap-2 text-zinc-500 text-sm"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading productivity pulse…
      </article>
    );
  }

  if (!pulse) return null;

  const days = pulse.days || [];
  const total = pulse.total || 0;
  const top = pulse.top_contributors || [];
  const maxCount = Math.max(1, ...days.map((d) => d.count || 0));

  // Sparkline coordinate math
  const innerW = SVG_W - PAD_X * 2;
  const innerH = SVG_H - PAD_Y * 2;
  const stepX = days.length > 1 ? innerW / (days.length - 1) : 0;
  const points = days.map((d, i) => {
    const x = PAD_X + i * stepX;
    const y = PAD_Y + innerH * (1 - (d.count || 0) / maxCount);
    return { x, y, count: d.count || 0, date: d.date };
  });
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  const areaPath = `${linePath} L${PAD_X + (days.length - 1) * stepX},${SVG_H - PAD_Y} L${PAD_X},${SVG_H - PAD_Y} Z`;

  return (
    <article
      data-testid="productivity-pulse"
      className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
      style={{ borderLeftColor: "#34d399", borderLeftWidth: 3 }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-overline text-emerald-500/80 mb-1 flex items-center gap-1.5">
            <Activity className="w-3 h-3" /> Productivity Pulse · 7 days
          </div>
          <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
            {total} task{total === 1 ? "" : "s"} completed this week
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Across {pulse.project_count || 0} project
            {pulse.project_count === 1 ? "" : "s"}
            {total === 0 && " — let's get something across the line."}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="digest-preview-btn"
              disabled={sending}
              onClick={() => handleSendDigest(true)}
              className="bg-transparent border-zinc-800 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
              <span className="ml-1.5 text-xs">Preview Digest</span>
            </Button>
            <Button
              type="button"
              size="sm"
              data-testid="digest-send-btn"
              disabled={sending || total === 0}
              onClick={() => handleSendDigest(false)}
              className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm disabled:opacity-40"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              <span className="ml-1.5 text-xs font-medium">Send Now</span>
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
        {/* Sparkline */}
        <svg
          data-testid="pulse-sparkline"
          width={SVG_W}
          height={SVG_H}
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          className="shrink-0"
        >
          <defs>
            <linearGradient id="pulseGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#34d399" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {/* Zero baseline */}
          <line
            x1={PAD_X}
            y1={SVG_H - PAD_Y}
            x2={SVG_W - PAD_X}
            y2={SVG_H - PAD_Y}
            stroke="#27272a"
            strokeWidth={1}
          />
          {maxCount > 0 && total > 0 && (
            <>
              <path d={areaPath} fill="url(#pulseGradient)" />
              <path d={linePath} fill="none" stroke="#34d399" strokeWidth={1.5} />
            </>
          )}
          {/* Day dots — bigger on the latest day */}
          {points.map((p, i) => (
            <g key={p.date}>
              <circle
                cx={p.x}
                cy={p.y}
                r={i === points.length - 1 ? 3 : 2}
                fill={p.count > 0 ? "#34d399" : "#3f3f46"}
                stroke="#09090b"
                strokeWidth={1}
              />
              <title>{`${p.date}: ${p.count} task${p.count === 1 ? "" : "s"}`}</title>
            </g>
          ))}
          {/* Day-of-week labels (M T W T F S S) */}
          {points.map((p, i) => {
            const dow = new Date(p.date + "T12:00:00Z").toLocaleDateString("en-US", {
              weekday: "narrow",
              timeZone: "UTC",
            });
            return (
              <text
                key={`lbl-${p.date}`}
                x={p.x}
                y={SVG_H - 0}
                textAnchor="middle"
                fontSize={8}
                fill={i === points.length - 1 ? "#a1a1aa" : "#52525b"}
                fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace"
              >
                {dow}
              </text>
            );
          })}
        </svg>

        {/* Top contributors */}
        <div data-testid="pulse-contributors">
          <div className="text-overline text-zinc-500 mb-2">Top contributors</div>
          {top.length === 0 ? (
            <div className="text-xs italic text-zinc-600">
              {total > 0
                ? "Tasks completed without assignees."
                : "No completed tasks yet this week."}
            </div>
          ) : (
            <ol className="space-y-1.5">
              {top.map((c, i) => (
                <li
                  key={c.user_id}
                  data-testid={`contributor-row-${i + 1}`}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-mono text-zinc-500 w-5 shrink-0">
                      #{i + 1}
                    </span>
                    <span className="text-zinc-200 truncate">
                      {c.name || c.email || "Unknown"}
                    </span>
                  </span>
                  <span className="text-emerald-400 font-mono text-xs ml-3">
                    {c.count}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </article>
  );
}
